import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { RouterProvider } from "react-router-dom";

import { isDevelopmentEnv } from "@eroad/helpers/isDevelopmentEnv";
import { isTestRunEnv } from "@eroad/helpers/isTestRunEnv";
import { initGTM } from "@eroad/monitoring/gtm";
import { initPendo } from "@eroad/monitoring/pendo";
import { PageSafeError } from "@eroad/ui-library/Page";

import { getLocale } from "./helpers/getLocale";
import { reportError } from "./helpers/reportError";
import { router } from "./router";
import { sagaMiddleware, store } from "./store/store";
import { worker } from "./test/mocks/browser";

const init = async (): Promise<void> => {
  /**
   * This will catch and report errors with setting up Redux, Amplify, React-Router
   * or Internationalisation.
   */
  try {
    if (!isDevelopmentEnv() && !isTestRunEnv()) {
      const { shouldPolyfillFormatJS } = await import("@eroad/i18n/shouldPolyfillFormatJS");
      await shouldPolyfillFormatJS(getLocale());
    }

    // Prepend /tracking2 subdomain to Phoenix URLs
    // This is done because setting "basename" prop to Router does not automatically
    // prepend the basename to the URL as of RRv6. To view tracking2 subdomain it has
    // to be manually added, otherwise localhost:3000 would not render anything...
    const urlFragments = router.state.location.pathname.split("/");
    const hasTrackingSubdomain = urlFragments.some((fragment) => fragment.match(/tracking2/) !== null);
    if (!hasTrackingSubdomain) {
      // Trigger router navigation which appends the basename properly
      router.navigate(`/${urlFragments.join("/")}`, { replace: true });
    }

    sagaMiddleware.setContext({ router });

    ReactDOM.render(
      <Provider store={store}>
        <RouterProvider router={router} />
      </Provider>,
      document.getElementById("root"),
    );
  } catch (e) {
    reportError(e);
    ReactDOM.render(<PageSafeError showHeader />, document.getElementById("root"));
  }

  return undefined;
};

const initAppMocks = (): void => {
  if (isDevelopmentEnv() && process.env.SHOULD_MOCK_APIS) {
    worker.start({
      onUnhandledRequest: "bypass",
    });
  }
};

const initMonitoring = (): void => {
  initGTM();
  initPendo();
};

initAppMocks();
initMonitoring();
init().catch((error) => {
  reportError(error);
});
